import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import Spinner from '../common/spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../routes';
import { getHeaders, handleErrorResponse } from '../../lib/requests';
import PaginationControl from '../common/pagination_control';
import GeneralButton from '../common/button_general';
import TableHeader from '../common/table_header';
import GeneralizedTableRow from '../common/table_row';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import NoResultsFound from '../common/noresultsfound';
import { formatDate } from '../../lib/utils';

function RouteList() {
  const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting('asc_delivery_day_of_week');
  const navigate = useNavigate();
  const [routesData, setRoutesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQueryParam = queryParams.get('search');
  // Removed day_of_week query parameter
  // const dayOfWeekQueryParam = queryParams.get('day_of_week');

  const currentRoute = routes.find(route => route.path === location.pathname);
  const routePageTitle = currentRoute ? currentRoute.pageTitle : 'Routes';

  const [searchQuery] = useState(searchQueryParam || '');
  // Removed dayOfWeek state
  // const [dayOfWeek, setDayOfWeek] = useState(dayOfWeekQueryParam || '');

  const fetchRoutes = async () => {
    if (!pageSize) return;
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${apiUrl}/routes`, {
        headers: getHeaders(apiToken),
        params: { 
          page, 
          pageSize, 
          ...(sortBy && { order_by: sortBy }), 
          ...(searchQuery && { name: searchQuery })
          // Removed day_of_week parameter
        },
      });
      setTotalPages(response.data.totalPages);
      setTotalRecords(response.data.total);
      setRoutesData(response.data.data);
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {   
    fetchRoutes();
  }, [page, pageSize, sortBy, searchQuery, navigate]);

  const handleDelete = async (routeId) => {
    const confirmed = window.confirm("Are you sure you want to delete this route?");
    if (!confirmed) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      await axios.delete(`${apiUrl}/routes/${routeId}`, {
        headers: getHeaders(apiToken)
      });
      fetchRoutes();
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data.message &&
        error.response.data.message.includes("associated")
      ) {
        alert("You may only delete routes where there are no providers associated. Please remove the provider before deleting.");
      } else {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      }
    }
  };

  const pageTitle = routePageTitle || 'Routes';

  const columns = [
    { id: 'simple_id', name: 'ID' },
    { id: 'name', name: 'Name' },
    { id: 'reference_id', name: 'Reference ID' },
    { id: 'external_reference_id', name: 'External Reference ID' },
    { id: 'client_count', name: 'Provider Total' },
    {
      id: 'create_date',
      name: 'Created Date', format: (value) => formatDate(value)
    },
    {
      id: 'modified_date',
      name: 'Modified Date', format: (value) => formatDate(value)
    },
    {
      id: 'edit',
      link: (rowData) => `/routes/${rowData.route_id}`,
      tooltip: 'Edit Route',
      icon: PencilSquareIcon,
    },
    {
      id: 'delete',
      action: (rowData) => handleDelete(rowData.route_id),
      tooltip: 'Delete Route',
      icon: TrashIcon,
    }
  ];

  return (
    <MasterLayout pageTitle={pageTitle} searchQuery={searchQuery}>
      {showError && <ErrorMessage message={errorMessage} />}
      <div className="px-2 sm:px-3 lg:px-3">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2 flex justify-between">
          <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
          <GeneralButton type="button" buttonType="primary" onClick={() => navigate('/routes/create')}>
            Add Route
          </GeneralButton>
        </div>
        {/* Removed RouteFilterComponent for day of week filtering */}
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2">
          {isLoading ? (
            <Spinner minHeight={630} />
          ) : routesData.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-300">
              <TableHeader columns={columns} handleSortClick={handleSortClick} currentSort={sortBy} />
              {routesData.map((route) => (
                <GeneralizedTableRow key={route.route_id} rowData={route} columns={columns} />
              ))}
            </table>
          ) : (
            <NoResultsFound message={`No ${pageTitle.toLowerCase()} found.`} minHeight={630} />
          )}
          <PaginationControl
            page={page}
            recordsPerPage={pageSize}
            totalRecords={totalRecords}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        </div>
      </div>
    </MasterLayout>
  );
}

export default RouteList;
